import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { 
    BarChart3, 
    TrendingUp, 
    Package, 
    Calendar, 
    DollarSign, 
    ShoppingCart,
    Loader2,
    AlertCircle,
    Trophy,
    Users
} from 'lucide-react';

/**
 * ReportsPage - Dashboard de reportes con estadísticas de ventas
 * Muestra ventas de los últimos 30 días y productos más vendidos
 */
export default function ReportsPage() {
    const [sales, setSales] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [preventasByVendedor, setPreventasByVendedor] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Formato de moneda chilena
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Formato de fecha en español
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-CL', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const [salesRes, productsRes, vendedoresRes] = await Promise.all([
                    apiFetch(`${API_URL}/api/reports/sales-by-day`),
                    apiFetch(`${API_URL}/api/reports/top-products`),
                    apiFetch(`${API_URL}/api/reports/preventas-by-vendedor`)
                ]);

                if (!salesRes.ok || !productsRes.ok || !vendedoresRes.ok) {
                    throw new Error('Error al cargar los reportes');
                }

                const [salesData, productsData, vendedoresData] = await Promise.all([
                    salesRes.json(),
                    productsRes.json(),
                    vendedoresRes.json()
                ]);

                setSales(salesData);
                setTopProducts(productsData);
                setPreventasByVendedor(vendedoresData);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, []);

    // Calcular totales
    const totalRevenue = sales.reduce((sum, day) => sum + parseInt(day.total_amount || 0), 0);
    const totalSalesCount = sales.reduce((sum, day) => sum + parseInt(day.total_sales || 0), 0);
    const avgDailySales = sales.length > 0 ? Math.round(totalRevenue / sales.length) : 0;

    if (loading) {
        return (
            <div className="page reports-page">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '400px',
                    gap: '1rem'
                }}>
                    <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
                    <p style={{ color: 'var(--text-secondary)' }}>Cargando reportes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page reports-page">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '400px',
                    gap: '1rem'
                }}>
                    <AlertCircle size={48} style={{ color: 'var(--danger-color)' }} />
                    <p style={{ color: 'var(--danger-color)' }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page reports-page">
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem',
                    marginBottom: '0.5rem'
                }}>
                    <BarChart3 size={28} />
                    Reportes
                </h1>
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Resumen de ventas y productos más vendidos
                </p>
            </div>

            {/* Summary Cards */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div className="report-card" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    padding: '1.25rem'
                }}>
                    <div style={{ 
                        background: 'rgba(79, 70, 229, 0.1)', 
                        borderRadius: '12px', 
                        padding: '0.75rem',
                        display: 'flex'
                    }}>
                        <DollarSign size={24} style={{ color: 'var(--primary-color)' }} />
                    </div>
                    <div>
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            fontSize: '0.875rem', 
                            margin: 0 
                        }}>
                            Ingresos Totales
                        </p>
                        <p style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700', 
                            margin: 0,
                            color: 'var(--text-main)'
                        }}>
                            {formatCurrency(totalRevenue)}
                        </p>
                    </div>
                </div>

                <div className="report-card" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    padding: '1.25rem'
                }}>
                    <div style={{ 
                        background: 'rgba(16, 185, 129, 0.1)', 
                        borderRadius: '12px', 
                        padding: '0.75rem',
                        display: 'flex'
                    }}>
                        <ShoppingCart size={24} style={{ color: 'var(--secondary-color)' }} />
                    </div>
                    <div>
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            fontSize: '0.875rem', 
                            margin: 0 
                        }}>
                            Total Ventas
                        </p>
                        <p style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700', 
                            margin: 0,
                            color: 'var(--text-main)'
                        }}>
                            {totalSalesCount}
                        </p>
                    </div>
                </div>

                <div className="report-card" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    padding: '1.25rem'
                }}>
                    <div style={{ 
                        background: 'rgba(245, 158, 11, 0.1)', 
                        borderRadius: '12px', 
                        padding: '0.75rem',
                        display: 'flex'
                    }}>
                        <TrendingUp size={24} style={{ color: 'var(--accent-color)' }} />
                    </div>
                    <div>
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            fontSize: '0.875rem', 
                            margin: 0 
                        }}>
                            Promedio Diario
                        </p>
                        <p style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700', 
                            margin: 0,
                            color: 'var(--text-main)'
                        }}>
                            {formatCurrency(avgDailySales)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Reports Grid */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
                gap: '1.5rem' 
            }}>
                {/* Sales by Day */}
                <div className="report-card">
                    <h2 style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        fontSize: '1.125rem',
                        marginBottom: '1rem'
                    }}>
                        <Calendar size={20} style={{ color: 'var(--primary-color)' }} />
                        Ventas por Día (Últimos 30 días)
                    </h2>
                    
                    {sales.length === 0 ? (
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            textAlign: 'center', 
                            padding: '2rem' 
                        }}>
                            No hay datos de ventas disponibles
                        </p>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Fecha</th>
                                        <th style={{ textAlign: 'center' }}>Cantidad</th>
                                        <th style={{ textAlign: 'right' }}>Ingresos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map((day, i) => (
                                        <tr key={i}>
                                            <td style={{ textAlign: 'left' }}>
                                                {formatDate(day.date)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    background: 'rgba(79, 70, 229, 0.1)',
                                                    color: 'var(--primary-color)',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {day.total_sales}
                                                </span>
                                            </td>
                                            <td style={{ 
                                                textAlign: 'right', 
                                                fontWeight: '600',
                                                color: 'var(--secondary-color)'
                                            }}>
                                                {formatCurrency(parseInt(day.total_amount))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Top Products */}
                <div className="report-card">
                    <h2 style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        fontSize: '1.125rem',
                        marginBottom: '1rem'
                    }}>
                        <Trophy size={20} style={{ color: 'var(--accent-color)' }} />
                        Top 10 Productos Más Vendidos
                    </h2>
                    
                    {topProducts.length === 0 ? (
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            textAlign: 'center', 
                            padding: '2rem' 
                        }}>
                            No hay datos de productos disponibles
                        </p>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', width: '50%' }}>Producto</th>
                                        <th style={{ textAlign: 'center' }}>Vendidos</th>
                                        <th style={{ textAlign: 'right' }}>Ingresos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topProducts.map((p, i) => (
                                        <tr key={i}>
                                            <td style={{ textAlign: 'left' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    {i < 3 && (
                                                        <span style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '700',
                                                            background: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : '#CD7F32',
                                                            color: i === 0 ? '#000' : '#fff'
                                                        }}>
                                                            {i + 1}
                                                        </span>
                                                    )}
                                                    <span style={{ 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '200px'
                                                    }}>
                                                        {p.nombre}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    background: 'rgba(16, 185, 129, 0.1)',
                                                    color: 'var(--secondary-color)',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {p.quantity_sold}
                                                </span>
                                            </td>
                                            <td style={{ 
                                                textAlign: 'right', 
                                                fontWeight: '600',
                                                color: 'var(--secondary-color)'
                                            }}>
                                                {formatCurrency(parseInt(p.revenue))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Preventas por Pre-vendedor */}
                <div className="report-card">
                    <h2 style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        fontSize: '1.125rem',
                        marginBottom: '1rem'
                    }}>
                        <Users size={20} style={{ color: 'var(--primary-color)' }} />
                        Ranking Pre-vendedores (Últimos 30 días)
                    </h2>
                    
                    {preventasByVendedor.length === 0 ? (
                        <p style={{ 
                            color: 'var(--text-secondary)', 
                            textAlign: 'center', 
                            padding: '2rem' 
                        }}>
                            No hay preventas registradas por pre-vendedores en este período
                        </p>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', width: '40%' }}>Vendedor</th>
                                        <th style={{ textAlign: 'center' }}>Preventas</th>
                                        <th style={{ textAlign: 'right' }}>Monto Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preventasByVendedor.map((v, i) => (
                                        <tr key={v.id}>
                                            <td style={{ textAlign: 'left' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    {i < 3 && (
                                                        <span style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '700',
                                                            background: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : '#CD7F32',
                                                            color: i === 0 ? '#000' : '#fff'
                                                        }}>
                                                            {i + 1}
                                                        </span>
                                                    )}
                                                    <div>
                                                        <span style={{ fontWeight: '600' }}>{v.name}</span>
                                                        {v.role_name && (
                                                            <span style={{ 
                                                                display: 'block', 
                                                                fontSize: '0.75rem', 
                                                                color: 'var(--text-secondary)' 
                                                            }}>
                                                                {v.role_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    background: 'rgba(79, 70, 229, 0.1)',
                                                    color: 'var(--primary-color)',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {v.total_preventas}
                                                </span>
                                            </td>
                                            <td style={{ 
                                                textAlign: 'right', 
                                                fontWeight: '600',
                                                color: 'var(--secondary-color)'
                                            }}>
                                                {formatCurrency(parseInt(v.total_monto) || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
